/**
 * Plane geometry + the 2D similarity algebra the plan editor works in.
 *
 * Everything here is millimetres, Y-down — the same convention as
 * `internal/transform2d` on the backend, so a transform round-trips through
 * the API untouched: `p' = scale · R(rotation_deg) · p + t`, and a positive
 * rotation turns clockwise on screen.
 *
 * Pure functions, no React, no DOM: this module is the unit-testable core of
 * the editor (spec 2026-08-22-02 §Implementation plan 1).
 */

export interface Point {
    x: number
    y: number
}

/** The wire shape of `FrameTransform` — kept structurally identical on purpose. */
export interface Transform {
    tx: number
    ty: number
    rotation_deg: number
    scale: number
}

export const IDENTITY: Transform = { tx: 0, ty: 0, rotation_deg: 0, scale: 1 }

export const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v))

export const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y)

/**
 * Formats a number for an SVG attribute: 7 significant digits, no exponent,
 * -0 normalized. Significant — not fixed — digits, because a frame's unit is
 * whatever its drawing surface uses: millimetres for a floorplan-v1 import,
 * a fraction of the plan's width for a raster plan (spec 2026-08-25-03). Two
 * fixed decimals were 0.01 mm in the first case and 1 % of the plan in the
 * second (spec 2026-09-07-04).
 */
export function fnum(v: number): string {
    if (!Number.isFinite(v)) return '0'
    const r = Number(v.toPrecision(7))
    if (r === 0) return '0' // covers -0 and (with toPrecision rounding) exact underflow
    if (Math.abs(r) >= 1e-6) return String(r)
    // `String()` switches to exponent notation below 1e-6; spell those out.
    // A value can still round to all-zero at 9 fixed decimals (e.g. 3e-10) —
    // that must read as "0", never as a bare trailing "." or "-0.".
    const fixed = r.toFixed(9).replace(/0+$/, '')
    return fixed.endsWith('.') ? '0' : fixed
}

export const toRad = (deg: number): number => (deg * Math.PI) / 180
export const toDeg = (rad: number): number => (rad * 180) / Math.PI

/** Normalizes an angle into [0, 360). */
export function normalizeDeg(deg: number): number {
    const d = deg % 360
    return d < 0 ? d + 360 : d
}

export function isIdentity(t: Transform): boolean {
    return t.tx === 0 && t.ty === 0 && normalizeDeg(t.rotation_deg) === 0 && t.scale === 1
}

export function applyTransform(t: Transform, p: Point): Point {
    const r = toRad(t.rotation_deg)
    const cos = Math.cos(r)
    const sin = Math.sin(r)
    return {
        x: t.scale * (p.x * cos - p.y * sin) + t.tx,
        y: t.scale * (p.x * sin + p.y * cos) + t.ty,
    }
}

export function invertTransform(t: Transform): Transform {
    const scale = t.scale || 1
    const inv = 1 / scale
    const r = toRad(-t.rotation_deg)
    const cos = Math.cos(r)
    const sin = Math.sin(r)
    // p = R(-θ)/s · (p' - t)
    return {
        tx: -inv * (t.tx * cos - t.ty * sin),
        ty: -inv * (t.tx * sin + t.ty * cos),
        rotation_deg: -t.rotation_deg,
        scale: inv,
    }
}

/** `compose(outer, inner)` applies `inner` first, then `outer`. */
export function composeTransform(outer: Transform, inner: Transform): Transform {
    const shifted = applyTransform(outer, { x: inner.tx, y: inner.ty })
    return {
        tx: shifted.x,
        ty: shifted.y,
        rotation_deg: outer.rotation_deg + inner.rotation_deg,
        scale: outer.scale * inner.scale,
    }
}

export interface Projection {
    pt: Point
    t: number
    d: number
}

/** Projection clamped to the segment. */
export function projectToSegment(p: Point, a: Point, b: Point): Projection {
    const vx = b.x - a.x
    const vy = b.y - a.y
    const len2 = vx * vx + vy * vy
    const t = len2 ? clamp(((p.x - a.x) * vx + (p.y - a.y) * vy) / len2, 0, 1) : 0
    const pt = { x: a.x + vx * t, y: a.y + vy * t }
    return { pt, t, d: dist(p, pt) }
}

/**
 * Projection onto the wall's *infinite* line — `t` may fall outside [0,1].
 * Openings legitimately sit in the gap BETWEEN two collinear wall segments
 * (the exp editor's gap-door convention), so clamping here would teleport them.
 */
export function projectToLine(p: Point, a: Point, b: Point): Projection {
    const vx = b.x - a.x
    const vy = b.y - a.y
    const len2 = vx * vx + vy * vy
    const t = len2 ? ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2 : 0
    const pt = { x: a.x + vx * t, y: a.y + vy * t }
    return { pt, t, d: dist(p, pt) }
}

/** Absolute polygon area (mm²) by the shoelace formula. */
export function shoelace(pts: Point[]): number {
    let a = 0
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length
        a += pts[i].x * pts[j].y - pts[j].x * pts[i].y
    }
    return Math.abs(a) / 2
}

export function centroid(pts: Point[]): Point {
    let a = 0
    let cx = 0
    let cy = 0
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length
        const c = pts[i].x * pts[j].y - pts[j].x * pts[i].y
        a += c
        cx += (pts[i].x + pts[j].x) * c
        cy += (pts[i].y + pts[j].y) * c
    }
    if (Math.abs(a) < 1e-9 || pts.length === 0) {
        if (!pts.length) return { x: 0, y: 0 }
        return {
            x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
            y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
        }
    }
    return { x: cx / (3 * a), y: cy / (3 * a) }
}

export function pointInPolygon(p: Point, pts: Point[]): boolean {
    let inside = false
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        if (
            pts[i].y > p.y !== pts[j].y > p.y &&
            p.x < ((pts[j].x - pts[i].x) * (p.y - pts[i].y)) / (pts[j].y - pts[i].y) + pts[i].x
        ) {
            inside = !inside
        }
    }
    return inside
}

/** Shortest distance from a point to a closed ring's edges. */
export function distanceToRing(p: Point, ring: Point[]): number {
    let best = Number.POSITIVE_INFINITY
    for (let i = 0; i < ring.length; i++) {
        const d = projectToSegment(p, ring[i], ring[(i + 1) % ring.length]).d
        if (d < best) best = d
    }
    return best
}

/**
 * How close to an edge a vertex counts as *on* it: a millionth of the ring's
 * extent, so the test holds whatever the frame's plan units are — the same
 * rule as the server's (`vector_value.go`).
 */
export function ringTolerance(ring: Point[]): number {
    const bb = bboxOfPoints(ring)
    return bb ? Math.max(Math.hypot(bb.x1 - bb.x0, bb.y1 - bb.y0), 1e-9) * 1e-6 : 1e-9
}

const orientation = (o: Point, a: Point, b: Point): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)

/**
 * Whether an edge of `a` properly crosses an edge of `b` — at one point inside
 * both. Edges that only touch, or run along each other, do not: that is a hole
 * against its room's wall, not through it.
 */
export function ringsCross(a: Point[], b: Point[]): boolean {
    for (let i = 0; i < a.length; i++) {
        const p1 = a[i]
        const p2 = a[(i + 1) % a.length]
        for (let j = 0; j < b.length; j++) {
            const p3 = b[j]
            const p4 = b[(j + 1) % b.length]
            const d1 = orientation(p3, p4, p1)
            const d2 = orientation(p3, p4, p2)
            const d3 = orientation(p1, p2, p3)
            const d4 = orientation(p1, p2, p4)
            if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true
        }
    }
    return false
}

/**
 * Whether ring `inner` lies within ring `outer`: no edge crossing, every vertex
 * inside or on an edge — a hole may touch its room, as in OGC Simple Features —
 * and `inner` the smaller of the two, so tracing the room's own outline is not
 * a hole of the whole room.
 */
export function ringInside(inner: Point[], outer: Point[], eps = ringTolerance(outer)): boolean {
    if (inner.length < 3 || outer.length < 3) return false
    if (ringsCross(inner, outer) || shoelace(inner) >= shoelace(outer)) return false
    return inner.every((p) => pointInPolygon(p, outer) || distanceToRing(p, outer) <= eps)
}

/**
 * Whether two rings' interiors do not meet: no edge crossing, and no vertex of
 * one strictly inside the other — nor its centroid, which is what tells two
 * identical rings apart from two neighbours.
 */
export function ringsDisjoint(a: Point[], b: Point[], eps = ringTolerance(a)): boolean {
    if (ringsCross(a, b)) return false
    const strictlyIn = (p: Point, r: Point[]) => pointInPolygon(p, r) && distanceToRing(p, r) > eps
    for (const [x, y] of [[a, b], [b, a]] as const) {
        if (x.some((p) => strictlyIn(p, y))) return false
        const c = centroid(x)
        if (pointInPolygon(c, x) && strictlyIn(c, y)) return false
    }
    return true
}

export interface BBox {
    x0: number
    y0: number
    x1: number
    y1: number
}

export function bboxOfPoints(pts: Point[]): BBox | null {
    if (!pts.length) return null
    let x0 = Infinity
    let y0 = Infinity
    let x1 = -Infinity
    let y1 = -Infinity
    for (const p of pts) {
        if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue
        x0 = Math.min(x0, p.x)
        y0 = Math.min(y0, p.y)
        x1 = Math.max(x1, p.x)
        y1 = Math.max(y1, p.y)
    }
    if (!Number.isFinite(x0)) return null
    return { x0, y0, x1, y1 }
}

/**
 * Square metres, for room labels — **converted through the frame's
 * calibration** (spec 2026-08-25-03 §3, spec 2026-08-25-04 §3).
 *
 * The editor's coordinates are *plan units*: nominal millimetres until
 * somebody measures a known distance on the plan. An uncalibrated frame
 * therefore asserts nothing about metres, and this prints raw square units
 * (`u²`) so the reader is never handed a nominal number dressed up as a
 * measurement — the "uncalibrated" badge next to it says why.
 */
export function formatAreaM2(units2: number, mmPerUnit?: number | null): string {
    if (!(typeof mmPerUnit === 'number' && Number.isFinite(mmPerUnit) && mmPerUnit > 0)) {
        const u2 = units2 * 1e-6
        return `${u2 >= 10 ? u2.toFixed(1) : u2.toFixed(2)} u²`
    }
    const m2 = units2 * mmPerUnit * mmPerUnit * 1e-6
    return `${m2 >= 10 ? m2.toFixed(1) : m2.toFixed(2)} m²`
}

/**
 * A real-world millimetre distance, read as a person would say it: metres past
 * a metre, millimetres below. Unlike {@link formatLengthMm} the input is
 * already millimetres — a declared distance, not plan units.
 */
export function formatDistanceMm(mm: number): string {
    const v = Math.abs(mm)
    if (v >= 1000) return `${(mm / 1000).toFixed(2)} m`
    return `${Math.round(mm)} mm`
}

/**
 * A length readout — millimetres through the calibration, raw plan units
 * without one. Wall lengths, opening widths and measure echoes all go through
 * this so a single rule governs every number the editor shows.
 */
export function formatLengthMm(units: number, mmPerUnit?: number | null): string {
    if (!(typeof mmPerUnit === 'number' && Number.isFinite(mmPerUnit) && mmPerUnit > 0)) {
        return `${Math.round(units)} u`
    }
    return `${Math.round(units * mmPerUnit)} mm`
}
